import React, { useState, useEffect } from 'react'
import { useDispatch, connect } from 'react-redux'
import RootState from 'typings/RootState'
import styled from 'styled-components'
import { RouteComponentProps } from 'react-router-dom'
import { BreadcrumbLink } from 'Components/Shared/BreadCrumb/BreadCrumb'
import SubnavBar from 'Components/Shared/SubnavBar'
import { HeaderRow } from 'Components/Shared/Table'
import Pagination from 'Components/Shared/Pagination'

import { PaginationType } from 'features/types/common'

import {
  userEmailSelector,
  serviceTokenSelector,
} from 'features/sensitive/sensitive.selector'

import { getReportList, exportReportList } from 'features/adminReport/unit/api'
import { sortingTask } from 'features/adminReport/unit/actions'
import { GetReportListReq, DataItem } from 'features/adminReport/unit/types'

import Menu from 'Components/MenuAdmin/AdminQCMenu'

import RowItem from './RowItem'
import { EventModal } from 'Components/Shared'
import Filter from 'Components/AdminQC/Report/Filter/UnitFilter'
import { toDateText } from 'utils/datetime'
import ReportUnitTab from 'Components/Shared/Tab/ReportUnitTab'
import { initPagination } from 'utils/helper'

const breadcrumbs: BreadcrumbLink[] = [
  {
    url: '/dashboard',
    title: 'Home',
    icon: '/home.png',
    active: true,
    showTitle: false,
  },
  {
    url: '/report/unit',
    title: 'รายงานตรวจมาตรฐาน (ยูนิต/แปลง)',
    active: false,
    showTitle: true,
  },
]

type Props = ReduxProps & DispatchProps & RouteComponentProps

const ReportCommissionList: React.ComponentType<Props> = ({
  UserEmail,
  token,

  dataList,
  event,
  history,
  filter: currentFilter,
  initFilter,
  sorting,
  dataExport,
  exportEvent,
}) => {
  const dispatch = useDispatch()
  const [enableModal, setEnableModal] = useState(false)
  const [pagination, setPagination] = useState<PaginationType>(initPagination())

  useEffect(() => {
    onFindReport()
  }, [pagination])

  const onFindReport = (
    isClearFilter: boolean = false,
    isExport: boolean = false
  ) => {
    setEnableModal(true)
    const filter = isClearFilter ? initFilter : currentFilter
    const req: GetReportListReq = {
      UserEmail,
      ProjectTypeID: filter.ProjectTypeID,
      StartDate: toDateText(filter.Date.StartDate || ''),
      EndDate: toDateText(filter.Date.FinishDate || ''),
      ProjectCode: filter.ProjectCode,
      UnitList: filter.UnitList,
      QCTypeList: filter.QCTypeList,
      CheckedByRole: filter.CheckedByRole,
      Page: isExport ? 1 : pagination.Page,
      RowPerPage: isExport ? dataList.Total : pagination.RowPerPage,
    }
    if (isExport) {
      dispatch(exportReportList(token, req))
    } else {
      dispatch(getReportList(token, req))
    }
  }

  const onSuccess = () => {
    setEnableModal(false)
  }

  const onSort = (name: string) => {
    dispatch(sortingTask({ name }))
  }

  const content = dataList.List.map((item: DataItem, index: number) => {
    return <RowItem key={index} item={item} />
  })

  return (
    <>
      <SubnavBar breadcrumbs={breadcrumbs} hideSearch={true} />
      <EventModal
        name="report-commissoning"
        event={[event, exportEvent]}
        enable={enableModal}
        successCalback={onSuccess}
      />
      <Menu selected={'report.unit'}>
        <MainPage>
          <Header>รายงานตรวจงานมาตรฐาน (ยูนิต / แปลง)</Header>
          <ReportUnitTab selected="ByUnit" />
          <Filter
            onSearch={(isClear: boolean = false) => {
              pagination.Page = 1
              setPagination({ ...pagination })
              onFindReport(isClear)
            }}
            isDownLoading={exportEvent.IsLoading}
            onExport={() => onFindReport(false, true)}
            exportData={dataExport}
          />
          <HeaderRow
            onSort={name => onSort(name)}
            sorting={sorting}
            items={columnsName}
          />
          {content}
          <Pagination
            currentPage={pagination.Page}
            totalRows={dataList.Total}
            perPage={pagination.RowPerPage}
            onChangeNumber={(page: number) => {
              pagination.RowPerPage = page
              setPagination({ ...pagination })
            }}
            onClickPage={(page: number) => {
              pagination.Page = page
              setPagination({ ...pagination })
            }}
          />
        </MainPage>
      </Menu>
    </>
  )
}

const MainPage = styled.div`
  padding: 40px 45px;
  width: 100%;
`

const Header = styled.div`
  margin-bottom: 27px;
  font-size: 28px;
  line-height: 1.07;
  font-family: 'DB Helvethaica X 75 Bd';
  color: #000;
`

const columnsName = [
  {
    title: 'โครงการ',
    span: 4,
    key: 'ProjectCode',
  },
  {
    title: 'ยูนิต',
    span: 2,
    key: 'Unit',
  },
  {
    title: 'หมวดงาน',
    span: 1,
    key: 'QCTypeName',
  },
  {
    title: 'ครั้งที่ตรวจ',
    span: 1,
    key: 'CheckNumber',
  },
  {
    /* 
      #Request ขอปรับ label จากวันที่ตรวจ / เป็นวันที่ส่งรายงาน แต่ยังใช้ Key เดิม 
      #Trello Card URL: https://trello.com/c/j9cG91Tp
    */
    title: 'วันที่ส่งรายงาน',
    span: 2,
    key: 'InspectDate',
  },
  {
    title: 'คะแนนหน้างาน',
    span: 2,
    key: 'InspectionScore',
  },
  {
    title: 'คะแนนเอกสาร',
    span: 2,
    key: 'DocumentScore',
  },
  {
    title: 'ผลการตรวจ',
    span: 3,
    key: 'CheckStatus',
  },
  {
    title: 'ตรวจโดย',
    span: 2,
    key: 'CheckedByRole',
  },
  {
    title: 'ชื่อผู้ตรวจ',
    span: 2,
    key: 'CheckedBy',
  },
]

const mapStateToProps = (state: RootState) => {
  return {
    dataList: state.adminReportUnit.DataList,
    event: state.adminReportUnit.Event,
    filter: state.adminReportUnit.Filter,
    initFilter: state.adminReportUnit.ClearFilter,
    sorting: state.adminReportUnit.sorting,
    UserEmail: userEmailSelector(state),
    token: serviceTokenSelector(state),
    dataExport: state.adminReportUnit.UnitDataExport,
    exportEvent: state.adminReportUnit.UnitExportEvent,
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>
const mapDispatchToProps = {}

type DispatchProps = typeof mapDispatchToProps

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReportCommissionList)
